/* The two things that happen to a contract on the exchange and used to have
   nowhere to land in the book.
 *
 * **Adding to it.** The only way to record scaling in was "Add a contract",
 * which writes a second row for what MEXC shows as one position — two entry
 * prices, neither of them the one you are actually trading against, and a
 * liquidation distance measured from the wrong number on both. This blends it
 * properly: what filled and what the exchange recomputed are asked for, the
 * average paid per contract is worked out here.
 *
 * **Correcting it.** A figure typed wrong had no fix but deleting the row and
 * building it again, which threw away whatever had already been realised on it.
 * A correction now says the book was wrong; it does not say anything was
 * bought or sold, so it realises nothing.
 *
 * Neither is a close. Closing is next door, and it is the one that books a
 * result. */

import { useEffect, useState } from 'react';
import { searchContracts, type ContractHit } from '../api/client';
import { price as fmtPrice } from '../lib/format';
import { decimal, isPositiveDecimal } from '../lib/number';
import { ClosePerp } from './ClosePerp';
import { addToPosition, correctPosition } from '../state/addToPerp';
import { useStore } from '../state/store';
import type { PerpView } from '../state/derive';
import type { Perp } from '../types';
import { dateTime, moneySigned } from '../lib/format';
import { undoLastClose } from '../state/closePerp';
import { liquidationFor } from '../state/liquidation';

type Mode = 'none' | 'add' | 'fix' | 'close' | 'undo';

/** All three actions on one contract, and the state saying which panel is up.
    Kept together so two cannot be open at once — a card offering to add to a
    position and to close it in the same breath is a card you have to read
    twice. */
export function PerpActions({ perp, say }: { perp: PerpView; say: (m: string) => void }) {
  const { state, putPerp, addToPerp, undoPerpClose } = useStore();
  const [mode, setMode] = useState<Mode>('none');
  const done = () => setMode('none');

  /* The stored record, not the view. A PerpView carries the mark, the
     unrealised figure and the distance to liquidation, all worked out a moment
     ago from the marks — writing those back would put a snapshot of one instant
     into the record that describes the position, where it would go stale, ride
     into the export and the sync blob, and be re-derived on top of anyway. */
  const stored = state.data.perps.find((x) => x.id === perp.id) ?? null;
  if (!stored) return null;

  if (mode === 'add') {
    return <AddPanel perp={perp} stored={stored} say={say} done={done} add={addToPerp} />;
  }
  if (mode === 'fix') {
    return <FixPanel perp={perp} stored={stored} say={say} done={done} put={putPerp} />;
  }
  if (mode === 'close') {
    return <ClosePerp perp={perp} say={say} open onOpen={() => setMode('close')} onDone={done} />;
  }
  if (mode === 'undo') {
    return <UndoPanel stored={stored} say={say} done={done} undo={undoPerpClose} />;
  }

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
      {stored.closedAt ? null : (
        <button className="btn btn-sm" onClick={() => setMode('add')}>Add to it</button>
      )}
      <button className="btn btn-sm btn-ghost" onClick={() => setMode('fix')}>Correct</button>
      {/* Only where there is something to take back. A close recorded with the
          wrong figure could be corrected nowhere until now. */}
      {(stored.closes?.length ?? 0) > 0 ? (
        <button className="btn btn-sm btn-ghost" onClick={() => setMode('undo')}>Undo last close</button>
      ) : null}
      {/* Nothing left to close, and `closePerp` refuses a closed contract anyway. */}
      {stored.closedAt ? null : (
        <ClosePerp perp={perp} say={say} open={false} onOpen={() => setMode('close')} onDone={done} />
      )}
    </div>
  );
}

/* ---------------- adding ---------------- */

function AddPanel({ perp, stored, say, done, add }: {
  /** For display: the mark and everything derived from it. */
  perp: PerpView;
  /** For writing: the record as it is stored. */
  stored: Perp;
  say: (m: string) => void;
  done: () => void;
  /** Writes the contract and posts the extra margin out of the wallet. */
  add: (p: Perp, addedMarginUsdt: number) => Promise<void>;
}) {
  const [margin, setMargin] = useState('');
  const [price, setPrice] = useState(perp.mark === null ? '' : String(perp.mark));
  const [liq, setLiq] = useState(String(perp.liqPrice));
  const [busy, setBusy] = useState(false);

  const ready = isPositiveDecimal(margin) && isPositiveDecimal(price) && isPositiveDecimal(liq);
  const preview = ready
    ? addToPosition(stored, {
        marginUsdt: decimal(margin), price: decimal(price), liqPrice: decimal(liq),
      })
    : null;

  const save = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      await add(preview.next, decimal(margin));
      say(`${perp.display} increased. Entry moved from ${fmtPrice(preview.previousEntry)} `
        + `to ${fmtPrice(preview.next.entryPrice)}, and the margin came out of the free balance.`);
      done();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ width: '100%', marginTop: 10 }}>
      <div className="field">
        <label htmlFor={`add-m-${perp.id}`}>Margin added, in USDT</label>
        <input id={`add-m-${perp.id}`} className="input" data-mono="true" inputMode="decimal"
               value={margin} onChange={(e) => setMargin(e.target.value)} />
        <div className="hint">
          At {perp.leverage}x this puts on{' '}
          {isPositiveDecimal(margin) ? (decimal(margin) * perp.leverage).toFixed(2) : '—'} USDT of
          notional. Leverage is a setting on the position, not on a fill. If you changed it,
          that is a correction rather than an addition.
        </div>
      </div>

      <div className="field">
        <label htmlFor={`add-p-${perp.id}`}>What it filled at</label>
        <input id={`add-p-${perp.id}`} className="input" data-mono="true" inputMode="decimal"
               value={price} onChange={(e) => setPrice(e.target.value)} />
        <div className="hint">
          Prefilled with the current mark. Replace it with the fill on the exchange, which is the
          price the average has to be built from.
        </div>
      </div>

      <div className="field">
        <label htmlFor={`add-l-${perp.id}`}>Liquidation price now</label>
        <input id={`add-l-${perp.id}`} className="input" data-mono="true" inputMode="decimal"
               value={liq} onChange={(e) => setLiq(e.target.value)} />
        <div className="hint">
          Read it off the exchange card. Adding margin moves this figure, and by how much is the
          exchange&rsquo;s own maintenance arithmetic. Working it out here would be inventing the
          number that decides when you get liquidated.
        </div>
      </div>

      {preview ? (
        <>
          <div className="kv">
            <span className="k">Average entry</span>
            <span className="v num">
              {fmtPrice(preview.previousEntry)} → {fmtPrice(preview.next.entryPrice)}
            </span>
          </div>
          <div className="kv">
            <span className="k">Margin</span>
            <span className="v num">
              {perp.marginUsdt.toFixed(2)} → {preview.next.marginUsdt.toFixed(2)} USDT
            </span>
          </div>
          <div className="kv">
            <span className="k">Notional at entry</span>
            <span className="v num">
              {perp.sizeUsdt.toFixed(2)} → {preview.next.sizeUsdt.toFixed(2)} USDT
            </span>
          </div>
          <div className="dim" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.45 }}>
            The average is weighted by contracts, not by the two prices: {preview.addedContracts.toFixed(4)}{' '}
            added to {(perp.sizeUsdt / perp.entryPrice).toFixed(4)} already held. Check it against
            the exchange card before you rely on it.
          </div>
        </>
      ) : null}

      <div className="fab-row" style={{ marginTop: 10 }}>
        <button className="btn btn-block" onClick={done}>Cancel</button>
        <button className="btn btn-primary btn-block" disabled={!preview || busy}
                onClick={() => void save()}>
          {busy ? 'Saving…' : 'Record the addition'}
        </button>
      </div>
    </div>
  );
}

/* ---------------- correcting ---------------- */

function FixPanel({ perp, stored, say, done, put }: {
  perp: PerpView;
  stored: Perp;
  say: (m: string) => void;
  done: () => void;
  put: (p: Perp) => Promise<void>;
}) {
  /* The contract itself, because the pair is the thing most easily got wrong:
     the same company is listed against USDT and against USDC, and a position
     recorded on the wrong one carries every figure correctly and takes its
     marks from somewhere else. Searched rather than typed, so what lands on
     the record is the exact API symbol. */
  const [symbol, setSymbol] = useState(perp.symbol);
  const [display, setDisplay] = useState(perp.display);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<ContractHit[]>([]);
  const [side, setSide] = useState<'long' | 'short'>(perp.side);
  const [leverage, setLeverage] = useState(String(perp.leverage));
  const [margin, setMargin] = useState(String(perp.marginUsdt));
  const [entry, setEntry] = useState(String(perp.entryPrice));
  const [liq, setLiq] = useState(perp.liqEstimated ? '' : String(perp.liqPrice));
  const [tp, setTp] = useState(perp.takeProfit ? String(perp.takeProfit) : '');
  const [busy, setBusy] = useState(false);

  const liquidation = liquidationFor(decimal(liq), side, decimal(entry), decimal(leverage));
  const corrected = correctPosition(stored, {
    symbol,
    display,
    side,
    leverage: decimal(leverage),
    marginUsdt: decimal(margin),
    entryPrice: decimal(entry),
    liqPrice: liquidation?.price ?? Number.NaN,
    takeProfit: isPositiveDecimal(tp) ? decimal(tp) : undefined,
  });
  /* An estimate stays marked as one; a typed figure takes the mark off. */
  const next = corrected ? { ...corrected } : null;
  if (next) {
    if (liquidation?.estimated) next.liqEstimated = true;
    else delete next.liqEstimated;
  }

  useEffect(() => {
    const q = query.trim();
    if (q.length < 1) { setHits([]); return; }
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => {
      void searchContracts(q, ctrl.signal)
        .then((rows) => { if (!ctrl.signal.aborted) setHits(rows); })
        .catch(() => { /* the field still works as a plain box */ });
    }, 220);
    return () => { window.clearTimeout(timer); ctrl.abort(); };
  }, [query]);

  const save = async () => {
    if (!next) return;
    setBusy(true);
    try {
      await put(next);
      say(`${perp.display} corrected. Nothing was realised.`);
      done();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ width: '100%', marginTop: 10 }}>
      <div className="field">
        <label htmlFor={`fix-sym-${perp.id}`}>Contract</label>
        <input id={`fix-sym-${perp.id}`} className="input" autoComplete="off"
               placeholder="search to change the pair: MU, gold, oil…"
               value={query || `${display} · ${symbol}`}
               onChange={(e) => setQuery(e.target.value)} />
        {hits.length > 0 ? (
          <div className="suggestions">
            {hits.map((h) => (
              <button key={h.symbol} className="suggestion"
                      onClick={() => {
                        setSymbol(h.symbol);
                        setDisplay(h.base || h.symbol);
                        setQuery('');
                        setHits([]);
                      }}>
                <span className="suggestion-symbol num">{h.symbol}</span>
                <span className="suggestion-name">{h.name}</span>
              </button>
            ))}
          </div>
        ) : null}
        <div className="hint">
          The API symbol, not the name on the position card. <span className="num">MU_USDT</span> on
          screen is <span className="num">MUSTOCK_USDT</span> here. Ask for the displayed name and the
          exchange answers &ldquo;contract does not exist&rdquo;, which arrives as a silent missing
          mark rather than an error.
        </div>
      </div>

      <div className="field">
        <label htmlFor={`fix-s-${perp.id}`}>Side</label>
        <select id={`fix-s-${perp.id}`} className="select" value={side}
                onChange={(e) => setSide(e.target.value as 'long' | 'short')}>
          <option value="long">Long</option>
          <option value="short">Short</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div className="field" style={{ flex: 1, minWidth: 0 }}>
          <label htmlFor={`fix-lev-${perp.id}`}>Leverage</label>
          <input id={`fix-lev-${perp.id}`} className="input" data-mono="true" inputMode="decimal"
                 value={leverage} onChange={(e) => setLeverage(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1.3, minWidth: 0 }}>
          <label htmlFor={`fix-m-${perp.id}`}>Margin (USDT)</label>
          <input id={`fix-m-${perp.id}`} className="input" data-mono="true" inputMode="decimal"
                 value={margin} onChange={(e) => setMargin(e.target.value)} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div className="field" style={{ flex: 1, minWidth: 0 }}>
          <label htmlFor={`fix-e-${perp.id}`}>Entry</label>
          <input id={`fix-e-${perp.id}`} className="input" data-mono="true" inputMode="decimal"
                 value={entry} onChange={(e) => setEntry(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1, minWidth: 0 }}>
          <label htmlFor={`fix-l-${perp.id}`}>Liquidation</label>
          <input id={`fix-l-${perp.id}`} className="input" data-mono="true" inputMode="decimal"
                 placeholder="estimate it"
                 value={liq} onChange={(e) => setLiq(e.target.value)} />
        </div>
      </div>

      {liquidation?.estimated ? (
        <div className="hint" style={{ marginTop: -6, marginBottom: 12 }}>
          Liquidation left empty, so estimated at <span className="num">≈ {liquidation.price.toFixed(2)}</span>{' '}
          from the entry and the leverage, and shown as an estimate. Type the exchange&rsquo;s figure
          to replace it.
        </div>
      ) : null}

      <div className="field">
        <label htmlFor={`fix-tp-${perp.id}`}>Take profit</label>
        <input id={`fix-tp-${perp.id}`} className="input" data-mono="true" inputMode="decimal"
               placeholder="leave blank for none"
               value={tp} onChange={(e) => setTp(e.target.value)} />
      </div>

      <div className="kv">
        <span className="k">Notional at entry</span>
        <span className="v num">
          {next ? `${next.sizeUsdt.toFixed(2)} USDT` : '—'}
        </span>
      </div>
      <div className="note" style={{ marginTop: 8 }}>
        The notional is margin times leverage and is worked out from the two above rather than
        typed, never the &ldquo;Size (USDT)&rdquo; figure on the exchange card, which is contracts
        at the current mark and drifts every tick.
        <br />
        This changes what the book says about the contract. It realises nothing and books no
        trade. Use <strong>Close it</strong> for something you actually closed, and{' '}
        <strong>Add to it</strong> for something you added.
      </div>

      <div className="fab-row" style={{ marginTop: 10 }}>
        <button className="btn btn-block" onClick={done}>Cancel</button>
        <button className="btn btn-primary btn-block" disabled={!next || busy}
                onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save the correction'}
        </button>
      </div>
      {!next ? (
        <div className="hint" style={{ marginTop: 8 }}>
          Leverage, margin and entry need a figure above zero. Liquidation is estimated if left empty.
        </div>
      ) : null}
    </div>
  );
}

/* ---------------- taking the last close back ---------------- */

/* A close is four things at once — an event, the margin released, the result
   added up and the money moved — and until now a wrong figure in any of them
   was permanent: the panel that records a close cannot edit one, and a contract
   closed out had no buttons left at all. This puts the contract back exactly as
   it stood, so the close can be recorded again with the right number.
 *
 * What it does not do is guess about money. A close made after MEXC had already
 * settled it left the balance alone, and taking that back must leave it alone
 * too; closes recorded before that was written down say nothing either way, so
 * the switch is on screen with the figure it would move. */
function UndoPanel({ stored, say, done, undo }: {
  stored: Perp;
  say: (m: string) => void;
  done: () => void;
  undo: (id: string, opts?: { moveWallet?: boolean }) => Promise<void>;
}) {
  const outcome = undoLastClose(stored);
  const known = outcome?.event.settled !== undefined;
  const [moveWallet, setMoveWallet] = useState(outcome ? !outcome.event.settled : true);
  const [busy, setBusy] = useState(false);

  if (!outcome) {
    return (
      <div style={{ width: '100%', marginTop: 10 }}>
        <div className="note">Nothing has been closed on this contract yet.</div>
        <button className="btn btn-block" style={{ marginTop: 10 }} onClick={done}>Back</button>
      </div>
    );
  }

  const { event, next } = outcome;
  const whole = event.portion >= 0.999;
  const money = event.releasedUsdt + event.resultUsdt;

  const apply = async () => {
    setBusy(true);
    try {
      await undo(stored.id, { moveWallet });
      say(`${stored.display}: the close of ${dateTime(event.at)} was taken back`
        + (moveWallet ? `, and ${money.toFixed(2)} USDT came back out of the wallet.` : '.'));
      done();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ width: '100%', marginTop: 10 }}>
      <div className="kv">
        <span className="k">Closed</span>
        <span className="v num">{dateTime(event.at)}</span>
      </div>
      <div className="kv">
        <span className="k">{whole ? 'All of it' : `${Math.round(event.portion * 100)}% of it`}</span>
        <span className={`v num ${event.resultUsdt >= 0 ? 'up' : 'down'}`}>
          {moneySigned(event.resultUsdt, 'USD')}
        </span>
      </div>
      <div className="kv">
        <span className="k">Margin it released</span>
        <span className="v num">{event.releasedUsdt.toFixed(2)} USDT</span>
      </div>

      <div className="note" style={{ marginTop: 10 }}>
        Taking it back restores the contract as it stood before:{' '}
        <span className="num">{next.marginUsdt.toFixed(2)} USDT</span> of margin on{' '}
        <span className="num">{next.sizeUsdt.toFixed(2)} USDT</span> of notional
        {whole ? ', and the contract counts as open again' : ''}
        {typeof next.realizedUsdt === 'number'
          ? <>, with <span className="num">{next.realizedUsdt.toFixed(2)} USDT</span> still realised on it</>
          : ', with nothing realised on it'}. Nothing else is touched, and the close can be
        recorded again with the right figure.
      </div>

      <button className="btn btn-block" style={{ marginTop: 10 }} aria-pressed={moveWallet}
              onClick={() => setMoveWallet((m) => !m)}>
        {moveWallet
          ? `Take ${money.toFixed(2)} USDT back out of the wallet`
          : 'Leave the wallet alone'}
      </button>
      <div className="hint" style={{ marginTop: 6 }}>
        {known
          ? (event.settled
            ? 'This close left the balance alone, because MEXC had already settled it, so taking it back leaves the balance alone too.'
            : 'This close put that money into the wallet, so taking it back takes the same figure out.')
          : 'This close was recorded before the book kept track of which of the two it was. If the figure on the wallet already came from MEXC after it closed, leave the wallet alone.'}
      </div>

      <div className="fab-row" style={{ marginTop: 10 }}>
        <button className="btn btn-block" onClick={done}>Cancel</button>
        <button className="btn btn-danger btn-block" disabled={busy} onClick={() => void apply()}>
          {busy ? 'Undoing…' : 'Undo that close'}
        </button>
      </div>
    </div>
  );
}
